import React, { useState, useEffect } from 'react';
import { useTransactions } from '../../hooks/useTransactions';
import { solanaUtils } from '../../utils/solanaUtils';
import { validateWalletAddress } from '../../utils/withdrawUtils';
import { useWallet } from '../../contexts/WalletProvider';

import './Wallet.css';

// Constants
const NOTIFICATION_DURATION = 2000;

// Types
interface WalletProps {
  className?: string;
  onLogout?: () => void;
}

// Utility Functions
const formatters = {
  solBalancePrecise: (value: number): string => {
    return `${value.toFixed(6)} SOL`;
  },

  address: (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-8)}`;
  }
};

// Custom Hooks

const useDropdownControl = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.wallet')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return { isDropdownOpen, setIsDropdownOpen };
};

const useClipboard = () => {
  const [showCopyNotification, setShowCopyNotification] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyNotification(true);
      setTimeout(() => setShowCopyNotification(false), NOTIFICATION_DURATION);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  return { showCopyNotification, copyToClipboard };
};

// Sub-components  
const LoadingState: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`wallet loading ${className || ''}`}>
    <div className="wallet-summary">
      <div className="wallet-balance">
        <div className="loading-spinner"></div>
      </div>
    </div>
  </div>
);

const LoginButton: React.FC<{ onLogin: () => void }> = ({ onLogin }) => (
  <button onClick={onLogin} className="login-button">
     CONNECT
  </button>
);

const WalletSummary: React.FC<{
  solBalance: number | null;
  isLoadingBalance: boolean;
}> = ({ solBalance, isLoadingBalance }) => {
  
  return (
    <div className="wallet-summary">
      <img 
        src="/assets/icons/solanaLogoMark.svg" 
        alt="SOL" 
        className="wallet-sol-icon"
      />
      <div className="wallet-balance">
        {isLoadingBalance ? (
          <span className="balance-loading">...</span>
        ) : (
          <span className="balance-amount">
            {solBalance !== null ? solBalance.toFixed(4) : '--'}
          </span>
        )}
      </div>
    </div>
  );
};



const WalletDropdown: React.FC<{
  solBalance: number | null;
  isLoadingBalance: boolean;
  solanaWallet: any;
  showCopyNotification: boolean;
  showWithdrawForm: boolean;
  withdrawAddress: string;
  withdrawAmount: string;
  isWithdrawing: boolean;
  onCopyAddress: () => void;
  onLogout: () => void;
  onClose: () => void;
  onWithdrawClick: () => void;
  onWithdrawCancel: () => void;
  onWithdrawSubmit: () => void;
  onWithdrawAddressChange: (value: string) => void;
  onWithdrawAmountChange: (value: string) => void;
  getMaxWithdrawable?: () => number;
}> = ({ 
  solBalance, 
  isLoadingBalance, 
  solanaWallet, 
  showCopyNotification,
  showWithdrawForm,
  withdrawAddress,
  withdrawAmount,
  isWithdrawing,
  onCopyAddress,
  onLogout,
  onClose,
  onWithdrawClick,
  onWithdrawCancel,
  onWithdrawSubmit,
  onWithdrawAddressChange,
  onWithdrawAmountChange,
  getMaxWithdrawable
}) => (
  <div className="wallet-dropdown">
    <div className="dropdown-header">
      <div className="user-details">
        <div className="balance-detail">
          <span className="network-indicator">
            {solanaUtils.getNetworkName()} • Live Mode
          </span>
          <span className="balance-amount-large">
            {isLoadingBalance ? 'Loading...' : formatters.solBalancePrecise(solBalance || 0)}
          </span>
        </div>
      </div>
    </div>

    <div className="dropdown-actions">
      {/* Wallet Address Copy Button */}
      {solanaWallet && (
        <>
          <button 
            className="address-copy-button" 
            title={`Click to copy: ${solanaWallet.address}`}
            onClick={onCopyAddress}
          >
            📋 {formatters.address(solanaWallet.address)}
          </button>
          
          {showCopyNotification && (
            <div className="copy-notification">
              ✅ Address copied to clipboard!
            </div>
          )}
        </>
      )}

      {/* Always show withdraw button since we're live mode only */}
      {solBalance !== null && solBalance > 0.001 && !showWithdrawForm && (
        <button 
          onClick={onWithdrawClick}
          className="withdraw-button"
        >
          Withdraw
        </button>
      )}
      
      {showWithdrawForm && (
        <div className="withdraw-form">
          <div className="withdraw-form-header">
            <span>Withdraw</span>
            <button onClick={onWithdrawCancel} className="withdraw-cancel">✕</button>
          </div>
          
          <div className="withdraw-form-field">
            <label>To Address:</label>
            <input
              type="text"
              value={withdrawAddress}
              onChange={(e) => onWithdrawAddressChange(e.target.value)}
              placeholder="Enter wallet address..."
              className="withdraw-input"
            />
          </div>
          
          <div className="withdraw-form-field">
            <label>Amount (SOL):</label>
            <div className="withdraw-amount-field">
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => onWithdrawAmountChange(e.target.value)}
                placeholder="0.000"
                step="0.001"
                min="0"
                max={solBalance?.toString()}
                className="withdraw-input"
              />
              <button 
                onClick={() => onWithdrawAmountChange(getMaxWithdrawable?.()?.toString() || '0')}
                className="withdraw-max-button"
              >
                MAX
              </button>
            </div>
            <span className="withdraw-max-label">Max: {getMaxWithdrawable?.()?.toFixed(4) || '0.0000'} SOL</span>
          </div>
          
          <div className="withdraw-form-actions">
            <button 
              onClick={onWithdrawSubmit}
              disabled={isWithdrawing || !withdrawAddress || !withdrawAmount}
              className="withdraw-submit"
            >
              {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
            </button>
          </div>
        </div>
      )}
      
      {!showWithdrawForm && (
        <button 
          onClick={() => {
            onLogout();
            onClose();
          }}
          className="logout-button"
        >
          Logout
        </button>
      )}
    </div>

  </div>
);

// Main Component
const Wallet: React.FC<WalletProps> = ({ className, onLogout }) => {
  const { 
    isAuthenticated,
    isReady,
    login,
    logout,
    address,
    balance,
    isLoading,
    solanaWallet,
    getMaxWithdrawable,
    refreshBalance
  } = useWallet();
  
  const { 
    withdrawSol
  } = useTransactions(solanaWallet);
  
  const { isDropdownOpen, setIsDropdownOpen } = useDropdownControl();
  const { showCopyNotification, copyToClipboard } = useClipboard();
  
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  // Clear component state when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setIsDropdownOpen(false);
      setShowWithdrawForm(false);
      setWithdrawAddress('');
      setWithdrawAmount('');
      setIsWithdrawing(false);
    }
  }, [isAuthenticated, setIsDropdownOpen]);

  const handleCopyAddress = () => {
    if (address) {
      copyToClipboard(address);
    }
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setShowWithdrawForm(false);
    setWithdrawAddress('');
    setWithdrawAmount('');
    setIsWithdrawing(false);
    
    if (onLogout) {
      onLogout();
    }
    logout();
  };

  const handleWithdrawClick = () => {
    setShowWithdrawForm(true);
  };

  const handleWithdrawCancel = () => {
    setShowWithdrawForm(false);
    setWithdrawAddress('');
    setWithdrawAmount('');
  };
  
  const handleWithdrawSubmit = async () => {
    if (!address || !balance || isWithdrawing) return;
    
    // Validate address
    const addressValidation = validateWalletAddress(withdrawAddress);
    if (!addressValidation.valid) {
      // Form validation - could add inline error display later
      return;
    }
    
    // Validate amount
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0 || amount > balance) {
      // Form validation - could add inline error display later
      return;
    }
    
    setIsWithdrawing(true);
    try {
      const result = await withdrawSol(withdrawAddress, amount);
      
      if (result.success) {
        
        // Wait a moment for confirmation, then refresh balance
        setTimeout(async () => {
          await refreshBalance();
        }, 2000);
        
        handleWithdrawCancel(); // Clear form on success
      } else {
        console.error('❌ Withdrawal failed:', result.error);
        // TODO: Show error to user with toast/modal
      }
    } catch (error: any) {
      console.error('❌ Withdrawal error:', error.message);
      // TODO: Show error to user with toast/modal
    } finally {
      setIsWithdrawing(false);
    }
  };

  // Render loading state
  if (!isReady) {
    return <LoadingState className={className} />;
  }

  return (
    <div className={`wallet authenticated ${className || ''}`}>
      {/* Show login button when not authenticated */}
      {!isAuthenticated ? (
        <LoginButton onLogin={login} />
      ) : (
        <div className="wallet-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <WalletSummary
            solBalance={balance}
            isLoadingBalance={isLoading}
          />
          <div className="dropdown-arrow">▼</div>
        </div>
      )}

      {/* Dropdown - show when authenticated */}
      {isAuthenticated && isDropdownOpen && (
        <WalletDropdown
          solBalance={balance}
          isLoadingBalance={isLoading}
          solanaWallet={solanaWallet}
          showCopyNotification={showCopyNotification}
          showWithdrawForm={showWithdrawForm}
          withdrawAddress={withdrawAddress}
          withdrawAmount={withdrawAmount}
          isWithdrawing={isWithdrawing}
          onCopyAddress={handleCopyAddress}
          onLogout={handleLogout}
          onWithdrawClick={handleWithdrawClick}
          onWithdrawCancel={handleWithdrawCancel}
          onWithdrawSubmit={handleWithdrawSubmit}
          onWithdrawAddressChange={setWithdrawAddress}
          onWithdrawAmountChange={setWithdrawAmount}
          onClose={() => setIsDropdownOpen(false)}
          getMaxWithdrawable={getMaxWithdrawable}
        />
      )}
    </div>
  );
};

export default Wallet; 